import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CircleNotch } from "@phosphor-icons/react";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Library from "@/pages/Library";
import Blog from "@/pages/Blog";
import ReadingList from "@/pages/ReadingList";
import Guide from "@/pages/Guide";
import GuideIndex from "@/pages/GuideIndex";
import ToolGuide from "@/pages/ToolGuide";
import Wizard from "@/pages/Wizard";
import Tracking from "@/pages/Tracking";
import EisenhowerMatrix from "@/pages/EisenhowerMatrix";
import EisenhowerView from "@/pages/EisenhowerView";
import CalibrationMatrix from "@/pages/CalibrationMatrix";
import CalibrationView from "@/pages/CalibrationView";
import CoachingNote from "@/pages/CoachingNote";
import CoachingView from "@/pages/CoachingView";
import CommunicationStrategy from "@/pages/CommunicationStrategy";
import CommunicationView from "@/pages/CommunicationView";
import PlayingToWin from "@/pages/PlayingToWin";
import PlayingToWinView from "@/pages/PlayingToWinView";
import Sipoc from "@/pages/Sipoc";
import SipocView from "@/pages/SipocView";
import Dmaic from "@/pages/Dmaic";
import DmaicTracking from "@/pages/DmaicTracking";
import Toc from "@/pages/Toc";
import TocView from "@/pages/TocView";
import FiveForces from "@/pages/FiveForces";
import FiveForcesView from "@/pages/FiveForcesView";
import Vrio from "@/pages/Vrio";
import VrioView from "@/pages/VrioView";
import Swot from "@/pages/Swot";
import SwotView from "@/pages/SwotView";
import LongPestel from "@/pages/LongPestel";
import LongPestelView from "@/pages/LongPestelView";
import Ccc from "@/pages/Ccc";
import CccTracking from "@/pages/CccTracking";
import Fmea from "@/pages/Fmea";
import FmeaView from "@/pages/FmeaView";
import AssetLifecycle from "@/pages/AssetLifecycle";
import AssetLifecycleView from "@/pages/AssetLifecycleView";
import Congruence from "@/pages/Congruence";
import CongruenceView from "@/pages/CongruenceView";
import NineBox from "@/pages/NineBox";
import NineBoxView from "@/pages/NineBoxView";
import KeyPersonRisk from "@/pages/KeyPersonRisk";
import KeyPersonView from "@/pages/KeyPersonView";
import RaciE from "@/pages/RaciE";
import RaciEView from "@/pages/RaciEView";
import Ansoff from "@/pages/Ansoff";
import AnsoffView from "@/pages/AnsoffView";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Pricing from "@/pages/Pricing";
import SharedDocument from "@/pages/SharedDocument";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400">
      <CircleNotch size={32} className="animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/library" element={<Protected><Library /></Protected>} />
      <Route path="/blog" element={<Protected><Blog /></Protected>} />
      <Route path="/reading-list" element={<Protected><ReadingList /></Protected>} />
      <Route path="/guide" element={<Protected><GuideIndex /></Protected>} />
      <Route path="/guide/focus-armor" element={<Protected><Guide /></Protected>} />
      <Route path="/guide/:type" element={<Protected><ToolGuide /></Protected>} />
      <Route path="/wizard/:id" element={<Protected><Wizard /></Protected>} />
      <Route path="/track/:id" element={<Protected><Tracking /></Protected>} />
      <Route path="/matrix/:id" element={<Protected><EisenhowerMatrix /></Protected>} />
      <Route path="/matrix/:id/view" element={<Protected><EisenhowerView /></Protected>} />
      <Route path="/calibration/:id" element={<Protected><CalibrationMatrix /></Protected>} />
      <Route path="/calibration/:id/view" element={<Protected><CalibrationView /></Protected>} />
      <Route path="/coaching/:id" element={<Protected><CoachingNote /></Protected>} />
      <Route path="/coaching/:id/view" element={<Protected><CoachingView /></Protected>} />
      <Route path="/communication/:id" element={<Protected><CommunicationStrategy /></Protected>} />
      <Route path="/communication/:id/view" element={<Protected><CommunicationView /></Protected>} />
      <Route path="/ptw/:id" element={<Protected><PlayingToWin /></Protected>} />
      <Route path="/ptw/:id/view" element={<Protected><PlayingToWinView /></Protected>} />
      <Route path="/sipoc/:id" element={<Protected><Sipoc /></Protected>} />
      <Route path="/sipoc/:id/view" element={<Protected><SipocView /></Protected>} />
      <Route path="/dmaic/:id" element={<Protected><Dmaic /></Protected>} />
      <Route path="/dmaic/:id/track" element={<Protected><DmaicTracking /></Protected>} />
      <Route path="/toc/:id" element={<Protected><Toc /></Protected>} />
      <Route path="/toc/:id/view" element={<Protected><TocView /></Protected>} />
      <Route path="/forces/:id" element={<Protected><FiveForces /></Protected>} />
      <Route path="/forces/:id/view" element={<Protected><FiveForcesView /></Protected>} />
      <Route path="/vrio/:id" element={<Protected><Vrio /></Protected>} />
      <Route path="/vrio/:id/view" element={<Protected><VrioView /></Protected>} />
      <Route path="/swot/:id" element={<Protected><Swot /></Protected>} />
      <Route path="/swot/:id/view" element={<Protected><SwotView /></Protected>} />
      <Route path="/pestel/:id" element={<Protected><LongPestel /></Protected>} />
      <Route path="/pestel/:id/view" element={<Protected><LongPestelView /></Protected>} />
      <Route path="/ccc/:id" element={<Protected><Ccc /></Protected>} />
      <Route path="/ccc/:id/track" element={<Protected><CccTracking /></Protected>} />
      <Route path="/fmea/:id" element={<Protected><Fmea /></Protected>} />
      <Route path="/fmea/:id/view" element={<Protected><FmeaView /></Protected>} />
      <Route path="/lifecycle/:id" element={<Protected><AssetLifecycle /></Protected>} />
      <Route path="/lifecycle/:id/view" element={<Protected><AssetLifecycleView /></Protected>} />
      <Route path="/congruence/:id" element={<Protected><Congruence /></Protected>} />
      <Route path="/congruence/:id/view" element={<Protected><CongruenceView /></Protected>} />
      <Route path="/ninebox/:id" element={<Protected><NineBox /></Protected>} />
      <Route path="/ninebox/:id/view" element={<Protected><NineBoxView /></Protected>} />
      <Route path="/keyperson/:id" element={<Protected><KeyPersonRisk /></Protected>} />
      <Route path="/keyperson/:id/view" element={<Protected><KeyPersonView /></Protected>} />
      <Route path="/raci/:id" element={<Protected><RaciE /></Protected>} />
      <Route path="/raci/:id/view" element={<Protected><RaciEView /></Protected>} />
      <Route path="/ansoff/:id" element={<Protected><Ansoff /></Protected>} />
      <Route path="/ansoff/:id/view" element={<Protected><AnsoffView /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/shared/:token" element={<SharedDocument />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
